import React, { useEffect, useState } from "react";

import moment from "moment";
import { renderToStaticMarkup } from "react-dom/server";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import ContentTooltipItem from "../timeline/ContentTooltipItem";

import type { JSX } from "react";

import type { InterstitialAdbreaks } from "../timeline/timeline.types";

interface AdbreakTimelineProps {
  startTime: string;
  endTime: string;
  interstitialAdbreaks: InterstitialAdbreaks;
  isLive?: boolean;
}

export default function AdbreakTimeline({
  startTime,
  endTime,
  interstitialAdbreaks,
  isLive,
}: AdbreakTimelineProps): JSX.Element {
  const { adbreakList } = interstitialAdbreaks;

  const adbreakDuration =
    new Date(adbreakList[0]?.endTime ?? "").getTime() -
    new Date(adbreakList[0]?.startTime ?? "").getTime();

  const mediaStartTime = new Date(startTime).getTime();
  const mediaEndTime =
    new Date(endTime).getTime() + adbreakDuration * adbreakList.length;
  const mediaDuration = mediaEndTime - mediaStartTime;

  const getNowMarkerleftPosition = (): number =>
    ((moment().valueOf() - mediaStartTime) / mediaDuration) * 100;

  const [nowMarkerLeftPosition, setNowMarkerLeftPosition] = useState(
    getNowMarkerleftPosition(),
  );

  useEffect(() => {
    if (!isLive) return () => {};

    const interval = setInterval(() => {
      const leftPosition = getNowMarkerleftPosition();

      setNowMarkerLeftPosition(leftPosition > 100 ? 0 : leftPosition);
    }, 500);

    return () => clearInterval(interval);
  });

  const adbreakMarkers = adbreakList.map((adbreak) => {
    const adbreakStartTime = new Date(adbreak.startTime).getTime();
    const adbreakEndTime = new Date(adbreak.endTime).getTime();

    const adbreakLeftPosition =
      ((adbreakStartTime - mediaStartTime) / mediaDuration) * 100;

    const adbreakWidth =
      ((adbreakEndTime - adbreakStartTime) / mediaDuration) * 100;

    const tooltipItem = (
      <ContentTooltipItem
        endTime={moment(adbreak.endTime)}
        startTime={moment(adbreak.startTime)}
      />
    );

    return (
      <div
        key={`${adbreak.startTime}-${adbreak.endTime}`}
        className="bg-status-pause absolute h-full"
        data-tooltip-html={renderToStaticMarkup(tooltipItem)}
        data-tooltip-id={APP_TOOLTIP_ID}
        style={{
          left: `${adbreakLeftPosition}%`,
          width: `${adbreakWidth}%`,
        }}
      />
    );
  });

  const renderNowIndicator = isLive && nowMarkerLeftPosition > 0 && (
    <div
      className="bg-destructive absolute top-1/2 h-[1.25rem] w-[2px] -translate-y-1/2 rounded-full transition-all"
      style={{
        left: `${nowMarkerLeftPosition}%`,
      }}
    />
  );

  return (
    <div className="relative w-full">
      <div className="border-border bg-card relative my-[0.78125rem] h-[0.78125rem] w-full overflow-hidden rounded-[0.3125rem] border drop-shadow-sm">
        {adbreakMarkers}
      </div>
      {renderNowIndicator}
    </div>
  );
}
